/**
 * Suggested product on a feature CTA — so Menu Builder / par / stock
 * open with a real line, not a blank form.
 */

import { isInternalOrFixtureTitle } from './growthEngineInternalTitle'
import { pickAcceptedSuggestedItem } from './growthEngineSuggestedData'

export const GROWTH_ENGINE_SUGGESTED_PRODUCT_PARAM = 'product'
export const GROWTH_ENGINE_SUGGESTED_PRODUCT_ID_PARAM = 'productId'
export const GROWTH_ENGINE_ORDER_SUCCESS_HANDOFF_PARAM = 'fromOrderSuccess'
/** Progress CTA — land on the job, not the marketing wall. */
export const GROWTH_ENGINE_PROGRESS_HANDOFF_PARAM = 'fromProgress'
/** Menu Builder: open create (or a named dish) after Progress. */
export const GROWTH_ENGINE_OPEN_CREATE_PARAM = 'create'
export const GROWTH_ENGINE_OPEN_RECIPE_PARAM = 'recipe'
/** Progress job feature id — Ask Ollie on the landing screen. */
export const GROWTH_ENGINE_PROGRESS_FEATURE_PARAM = 'progressFeature'

const ACTIONS_WITH_PRODUCT_IN_PLAY = new Set([
  'open_menu_builder',
  'open_par_levels',
  'start_stock_take',
])

export function growthEngineActionPutsProductInPlay(action) {
  return ACTIONS_WITH_PRODUCT_IN_PLAY.has(action)
}

/**
 * Next shopping-list line to put a par on: real title, no par yet.
 *
 * @param {object[]} [products]
 * @param {{ title?: string|null, productId?: string|null }|null} [prefer]
 * @returns {object|null}
 */
export function pickNextStapleForPar(products = [], prefer = null) {
  const list = (Array.isArray(products) ? products : []).filter(product => {
    if (!product?._id) return false
    const title = product.title || product.product?.title || ''
    if (isInternalOrFixtureTitle(title)) return false
    return true
  })
  if (!list.length) return null

  const hinted = findSuggestedVenueProduct(list, prefer)
  if (hinted && !(Number(hinted.parLevel) > 0)) return hinted

  const withoutPar = list.find(product => !(Number(product.parLevel) > 0))
  return withoutPar || hinted || list[0] || null
}

/**
 * Starter qty when Progress lands on a zero-par line (typical order, else 1).
 *
 * @param {object|null} [product]
 * @param {object|null} [pick]
 * @returns {string}
 */
export function suggestedParQtyForProgressHandoff(product, pick = null) {
  const typical = Number(pick?.typicalQuantity)
  if (Number.isFinite(typical) && typical > 0) return String(typical)
  const fromProduct = Number(
    product?.typicalQuantity ??
      product?.typicalOrderQuantity ??
      product?.lastOrderedQuantity,
  )
  if (Number.isFinite(fromProduct) && fromProduct > 0) {
    return String(fromProduct)
  }
  const current = Number(product?.parLevel)
  if (Number.isFinite(current) && current > 0) return String(current)
  return '1'
}

/**
 * @param {object|null|undefined} options
 * @returns {{ title: string|null, productId: string|null }}
 */
export function normalizeGrowthEngineSuggestedProduct(options = {}) {
  const title = String(
    options.product || options.productTitle || options.title || '',
  ).trim()
  if (isInternalOrFixtureTitle(title)) {
    return { title: null, productId: null }
  }
  const productId = String(options.productId || '').trim()
  return {
    title: title || null,
    productId: productId || null,
  }
}

/**
 * @param {string} path
 * @param {object} [options]
 * @returns {string}
 */
export function appendGrowthEngineSuggestedProduct(path, options = {}) {
  if (!path) return path
  const suggested = normalizeGrowthEngineSuggestedProduct(options)
  const [bare, existingQuery = ''] = String(path).split('?')
  const params = new URLSearchParams(existingQuery)
  if (suggested.title) {
    params.set(GROWTH_ENGINE_SUGGESTED_PRODUCT_PARAM, suggested.title)
  }
  if (suggested.productId) {
    params.set(GROWTH_ENGINE_SUGGESTED_PRODUCT_ID_PARAM, suggested.productId)
  }
  if (options.fromOrderSuccess) {
    params.set(GROWTH_ENGINE_ORDER_SUCCESS_HANDOFF_PARAM, '1')
  }
  if (options.fromProgress) {
    params.set(GROWTH_ENGINE_PROGRESS_HANDOFF_PARAM, '1')
  }
  if (options.fromProgress && options.featureId) {
    params.set(GROWTH_ENGINE_PROGRESS_FEATURE_PARAM, String(options.featureId))
  }
  if (options.openCreate) {
    params.set(GROWTH_ENGINE_OPEN_CREATE_PARAM, '1')
  }
  if (options.recipeId) {
    params.set(GROWTH_ENGINE_OPEN_RECIPE_PARAM, String(options.recipeId))
  }
  const qs = params.toString()
  return qs ? `${bare}?${qs}` : bare
}

/**
 * @param {string|null|undefined} search — location.search
 * @returns {object|null}
 */
export function parseGrowthEngineSuggestedProduct(search) {
  const raw = String(search || '').replace(/^\?/, '')
  if (!raw) return null
  const params = new URLSearchParams(raw)
  const suggested = normalizeGrowthEngineSuggestedProduct({
    product: params.get(GROWTH_ENGINE_SUGGESTED_PRODUCT_PARAM),
    productId: params.get(GROWTH_ENGINE_SUGGESTED_PRODUCT_ID_PARAM),
  })
  const fromOrderSuccess =
    params.get(GROWTH_ENGINE_ORDER_SUCCESS_HANDOFF_PARAM) === '1'
  const fromProgress = params.get(GROWTH_ENGINE_PROGRESS_HANDOFF_PARAM) === '1'
  const progressFeatureId = String(
    params.get(GROWTH_ENGINE_PROGRESS_FEATURE_PARAM) || '',
  ).trim()
  const openCreate = params.get(GROWTH_ENGINE_OPEN_CREATE_PARAM) === '1'
  const recipeId = String(
    params.get(GROWTH_ENGINE_OPEN_RECIPE_PARAM) || '',
  ).trim()
  if (
    !suggested.title &&
    !suggested.productId &&
    !fromOrderSuccess &&
    !fromProgress &&
    !openCreate &&
    !recipeId
  ) {
    return null
  }
  return {
    ...suggested,
    ...(fromOrderSuccess ? { fromOrderSuccess: true } : {}),
    ...(fromProgress ? { fromProgress: true } : {}),
    ...(progressFeatureId ? { featureId: progressFeatureId } : {}),
    ...(openCreate ? { openCreate: true } : {}),
    ...(recipeId ? { recipeId } : {}),
  }
}

/**
 * Product to start the first par on. Chip / URL hint wins; otherwise the
 * first shopping-list row. CTA must still work when there are no chips.
 *
 * @param {object} [params]
 * @param {object|null} [params.item] accepted suggested-data chip
 * @param {{ title?: string|null, productId?: string|null }|null} [params.suggestedProduct]
 * @param {object[]} [params.products]
 * @returns {{ product: object|null, pick: object|null, searchTitle: string }}
 */
export function pickProductForFirstPar({
  item,
  suggestedProduct,
  products = [],
} = {}) {
  const pick = pickAcceptedSuggestedItem(item)
  const hinted = {
    title: pick?.title || suggestedProduct?.title || null,
    productId: pick?.productId || suggestedProduct?.productId || null,
  }
  const list = Array.isArray(products) ? products : []
  if (suggestedProduct?.fromProgress && !hinted.title && !hinted.productId) {
    const staple = pickNextStapleForPar(list)
    if (staple) {
      return {
        product: staple,
        pick,
        searchTitle: staple.title || staple.product?.title || '',
      }
    }
  }
  const matched = findSuggestedVenueProduct(list, hinted)
  if (matched) {
    return {
      product: matched,
      pick,
      searchTitle:
        hinted.title || matched.title || matched.product?.title || '',
    }
  }
  const first = list.find(product => product && product._id) || null
  return { product: first, pick, searchTitle: '' }
}

/**
 * @param {object[]} [products] venue products
 * @param {{ title?: string|null, productId?: string|null }|null} suggested
 */
export function findSuggestedVenueProduct(products, suggested) {
  if (!suggested || !Array.isArray(products) || !products.length) {
    return null
  }
  const id = String(suggested.productId || '').trim()
  if (id) {
    const byId = products.find(
      product =>
        String(product?._id || '') === id ||
        String(product?.product?._id || '') === id,
    )
    if (byId) return byId
  }
  const title = String(suggested.title || '')
    .trim()
    .toLowerCase()
  if (!title) return null
  return (
    products.find(
      product =>
        String(product?.title || product?.product?.title || '')
          .trim()
          .toLowerCase() === title,
    ) || null
  )
}

/**
 * First-try create needs a chip or URL product in play so the form has a
 * title they can save. Blank CTA must not open an unsavable wizard.
 * After they already have a recipe, manual create stays allowed.
 * Progress `create=1` always may start (empty venue → Create New Recipe).
 */
export function canStartFirstRecipeCreate({
  item,
  suggestedProduct,
  products = [],
  hasExistingRecipes = false,
} = {}) {
  if (suggestedProduct?.fromProgress && suggestedProduct?.openCreate) {
    return true
  }
  const pick = pickAcceptedSuggestedItem(item)
  if (pick?.title || pick?.productId) return true
  if (suggestedProduct?.title || suggestedProduct?.productId) return true
  if (hasExistingRecipes) return true
  return (
    Array.isArray(products) &&
    products.some(product => product && (product._id || product.title))
  )
}

/**
 * Progress create handoff — wait until recipes (+ shopping list) are ready so
 * empty venues open Create New Recipe instead of flashing “No recipes found”.
 *
 * @returns {boolean} true when Dashboard should run beginFirstRecipe now
 */
export function shouldOpenProgressRecipeCreateHandoff({
  suggestedProduct,
  recipesLoading = false,
  venueProductsLoading = false,
  productsReady = true,
  allowCreate = false,
  needsProductFirst = false,
} = {}) {
  if (!suggestedProduct?.fromProgress) return false
  const wantsCreate =
    Boolean(suggestedProduct.openCreate) ||
    Boolean(suggestedProduct.title) ||
    Boolean(suggestedProduct.productId)
  if (!wantsCreate) return false
  if (recipesLoading || venueProductsLoading || !productsReady) return false
  // No products yet: beginFirstRecipe redirects to catalogue / suppliers.
  if (needsProductFirst) return true
  return Boolean(allowCreate)
}

export function buildFirstRecipeFormPrefill(suggestedProduct, products = []) {
  if (!suggestedProduct || typeof suggestedProduct !== 'object') return null
  const suggested = normalizeGrowthEngineSuggestedProduct(suggestedProduct)
  if (!suggested.title && !suggested.productId) return null
  const match = findSuggestedVenueProduct(products, suggested)
  const ingredient = match
    ? buildSuggestedIngredientFromVenueProduct(match)
    : {
        sku: '',
        title: suggested.title || '',
        size: '',
        price: '',
        UoM: '',
        totalPrice: '',
        ...(suggested.productId ? { productId: suggested.productId } : {}),
      }
  return {
    title: suggested.title || ingredient.title || '',
    ingredients: [ingredient],
  }
}

export function buildSuggestedIngredientFromVenueProduct(venueProduct) {
  const catalogue = venueProduct?.product || {}
  return {
    ...catalogue,
    productId: venueProduct?._id || catalogue._id || '',
    sku: catalogue.sku || '',
    title: venueProduct?.title || catalogue.title || '',
    size: '',
    conversions: venueProduct?.conversions || [],
    conversion: null,
    defaultSize: catalogue.size,
    UoM: catalogue.UoM || '',
    defaultUnit: catalogue.UoM,
    price: '',
    totalPrice: '',
  }
}
